import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  Box,
  CheckCircle2,
  ClipboardCheck,
  ShieldAlert,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ContextBanner } from "@/components/common/ContextBanner";
import { DataTable } from "@/components/common/DataTable";
import type { ColumnDef } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { StatCard } from "@/components/common/StatCard";
import { api } from "@/lib/api-client";
import { toRiskScore } from "@/lib/utils";
import { useTranslation } from "@/hooks/useTranslation";
import { auditActionLabel, auditResourceLabel } from "@/lib/audit-labels";
import type { AssessmentStatus, RiskLevel } from "@/types";

const STALE_TIME = 60_000; // 1 minute

// ─────────────────────────────────────────────
// Dashboard — Screen 1 (bi8Au)
//
// KPIs, risk distribution donut, status bar chart,
// top risks table, needs attention table, recent activity.
// Design: §8.5 Screen 1-2
// ─────────────────────────────────────────────

// ── API response types ────────────────────────

interface DashboardSummary {
  totalApplications: number;
  pendingReview: number;
  approved: number;
  highPlusRisk: number;
  alertMessage?: string;
}

interface RiskDistributionItem {
  level: RiskLevel;
  count: number;
  percentage: number;
}

interface StatusBreakdownItem {
  status: AssessmentStatus;
  count: number;
}

interface TopRiskItem {
  entityId: string;
  entityName: string;
  riskLevel: RiskLevel;
  score: number;
  assessmentId?: string;
}

interface ActivityItem {
  id: string;
  action: string;
  resourceType: string;
  resourceId: string;
  userId: string;
  userName: string;
  createdAt: string;
}

interface NeedsAttentionItem {
  assessmentId: string;
  entityName: string;
  schemaName: string;
  status: AssessmentStatus;
  assignedTo?: string;
  assignedToName?: string;
  updatedAt: string;
}

// Raw row shape from GET /assessments (list endpoint) used for Needs Attention
interface AssessmentListApiRow {
  id: string;
  status: AssessmentStatus;
  assignedTo?: string | null;
  assignedToName?: string | null;
  updatedAt: string;
  entity?: { name?: string } | null;
  schemaVersion?: { version?: number; schema?: { name?: string } | null } | null;
}

// ── Risk color map ────────────────────────────

const RISK_COLORS: Record<RiskLevel, string> = {
  LOW: "var(--success)",
  MEDIUM: "var(--warning)",
  HIGH: "var(--k-orange)",
  VERY_HIGH: "var(--k-purple)",
  CRITICAL: "var(--danger)",
};

// ── Status label keys (translated at render time) ──

const STATUS_LABEL_KEYS: Record<AssessmentStatus, string> = {
  DRAFT: "status.draft",
  SUBMITTED: "status.submitted",
  IN_REVIEW: "status.inReview",
  APPROVED: "status.approved",
  REJECTED: "status.rejected",
};

// ── Helper ────────────────────────────────────

function formatRelative(iso: string, t: (key: string, params?: Record<string, string | number>) => string) {
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return t("assessment.minutesAgo", { n: mins });
    const hours = Math.floor(mins / 60);
    if (hours < 24) return t("assessment.hoursAgo", { n: hours });
    const days = Math.floor(hours / 24);
    return t("assessment.daysAgo", { n: days });
  } catch {
    return iso;
  }
}

// ── Score progress bar ────────────────────────

function ScoreBar({ score, level }: { score: number; level: RiskLevel }) {
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-24 overflow-hidden rounded-full bg-hover">
        <div
          className="h-full rounded-full transition-all"
          style={{
            width: `${score}%`,
            backgroundColor: RISK_COLORS[level],
          }}
        />
      </div>
      <span className="text-caption text-text-muted">{score}%</span>
    </div>
  );
}

// ── Custom Pie Tooltip ────────────────────────

function PieTooltipContent({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; payload: RiskDistributionItem & { name: string } }>;
}) {
  const { t } = useTranslation();
  if (!active || !payload?.length || !payload[0]) return null;
  const item = payload[0].payload;
  return (
    <div className="rounded-card border border-border-default bg-card px-3 py-2 text-[13px]">
      <p className="font-medium text-text-primary">{item.name}</p>
      <p className="text-text-muted">
        {t("dashboard.appsWithPercent", {
          count: item.count,
          percentage: item.percentage.toFixed(1),
        })}
      </p>
    </div>
  );
}

// ── Bar Tooltip ───────────────────────────────

function BarTooltipContent({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ value: number }>;
  label?: string;
}) {
  const { t } = useTranslation();
  if (!active || !payload?.length || !payload[0]) return null;
  return (
    <div className="rounded-card border border-border-default bg-card px-3 py-2 text-[13px]">
      <p className="font-medium text-text-primary">{label}</p>
      <p className="text-text-muted">{payload[0].value} {t("dashboard.assessments")}</p>
    </div>
  );
}

// ── Main component ────────────────────────────

export function Dashboard() {
  const { t } = useTranslation();

  const RISK_LABELS: Record<RiskLevel, string> = {
    LOW: t("risk.low"),
    MEDIUM: t("risk.medium"),
    HIGH: t("risk.high"),
    VERY_HIGH: t("risk.veryHigh"),
    CRITICAL: t("risk.critical"),
  };

  const statusLabel = (status: AssessmentStatus) => {
    const key = STATUS_LABEL_KEYS[status];
    return key
      ? t(key)
      : status
          .replace(/_/g, " ")
          .toLowerCase()
          .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  // ── Table columns (inside component for translations) ──

  const topRisksColumns: ColumnDef<TopRiskItem>[] = [
    {
      key: "entityName",
      header: t("entity.name"),
      cell: (row) => (
        <span className="font-medium text-text-primary">{row.entityName}</span>
      ),
    },
    {
      key: "riskLevel",
      header: t("risk.level"),
      cell: (row) => <StatusBadge type="risk" status={row.riskLevel} />,
    },
    {
      key: "score",
      header: t("risk.score"),
      cell: (row) => <ScoreBar score={row.score} level={row.riskLevel} />,
    },
  ];

  const attentionColumns: ColumnDef<NeedsAttentionItem>[] = [
    {
      key: "entityName",
      header: t("entity.name"),
      cell: (row) => (
        <span className="font-medium text-text-primary">{row.entityName}</span>
      ),
    },
    {
      key: "schemaName",
      header: t("assessment.schema"),
      cell: (row) => (
        <span className="text-text-secondary">{row.schemaName}</span>
      ),
    },
    {
      key: "status",
      header: t("entity.status"),
      cell: (row) => <StatusBadge type="assessment" status={row.status} />,
    },
    {
      key: "assignedTo",
      header: t("assessment.assignedTo"),
      cell: (row) => (
        <span className="text-text-muted">{row.assignedToName ?? row.assignedTo ?? "-"}</span>
      ),
    },
    {
      key: "updatedAt",
      header: t("assessment.lastUpdated"),
      sortable: true,
      sortAccessor: (row) => new Date(row.updatedAt),
      cell: (row) => (
        <span className="text-caption text-text-muted">
          {formatRelative(row.updatedAt, t)}
        </span>
      ),
    },
  ];

  const activityColumns: ColumnDef<ActivityItem>[] = [
    {
      key: "action",
      header: t("auditLogs.action"),
      cell: (row) => (
        <span className="text-text-secondary">{auditActionLabel(t, row.action)}</span>
      ),
    },
    {
      key: "resourceType",
      header: t("auditLogs.resource"),
      cell: (row) => (
        <span className="text-text-muted">
          {auditResourceLabel(t, row.resourceType)}
        </span>
      ),
    },
    {
      key: "userName",
      header: t("users.name"),
      cell: (row) => (
        <span className="text-text-secondary">{row.userName}</span>
      ),
    },
    {
      key: "createdAt",
      header: t("auditLogs.timestamp"),
      sortable: true,
      sortAccessor: (row) => new Date(row.createdAt),
      cell: (row) => (
        <span className="text-caption text-text-muted">
          {formatRelative(row.createdAt, t)}
        </span>
      ),
    },
  ];

  const { data: summaryRes, isLoading: summaryLoading, isError: summaryError } = useQuery({
    queryKey: ["dashboard", "summary"],
    queryFn: () => api.get<{ data: DashboardSummary }>("/dashboard/summary"),
    staleTime: STALE_TIME,
  });

  const { data: distRes, isLoading: distLoading } = useQuery({
    queryKey: ["dashboard", "risk-distribution"],
    queryFn: () => api.get<{ data: RiskDistributionItem[] }>("/dashboard/risk-distribution"),
    staleTime: STALE_TIME,
  });

  const { data: statusRes, isLoading: statusLoading } = useQuery({
    queryKey: ["dashboard", "status-breakdown"],
    queryFn: () => api.get<{ data: StatusBreakdownItem[] }>("/dashboard/status-breakdown"),
    staleTime: STALE_TIME,
  });

  const { data: topRes, isLoading: topLoading } = useQuery({
    queryKey: ["dashboard", "top-risks"],
    queryFn: () => api.get<{ data: TopRiskItem[] }>("/dashboard/top-risks"),
    staleTime: STALE_TIME,
  });

  const { data: actRes, isLoading: actLoading } = useQuery({
    queryKey: ["dashboard", "recent-activity"],
    queryFn: () => api.get<{ data: ActivityItem[] }>("/dashboard/recent-activity"),
    staleTime: STALE_TIME,
  });

  // Needs Attention = assessments awaiting review (SUBMITTED + IN_REVIEW).
  // The list endpoint filters by a single status, so query each status separately
  // and merge. Read-only — reuses the existing GET /assessments endpoint.
  const { data: submittedRes, isLoading: submittedLoading } = useQuery({
    queryKey: ["dashboard", "needs-attention", "SUBMITTED"],
    queryFn: () =>
      api.get<{ data: AssessmentListApiRow[] }>("/assessments?status=SUBMITTED&pageSize=100"),
    staleTime: STALE_TIME,
  });

  const { data: inReviewRes, isLoading: inReviewLoading } = useQuery({
    queryKey: ["dashboard", "needs-attention", "IN_REVIEW"],
    queryFn: () =>
      api.get<{ data: AssessmentListApiRow[] }>("/assessments?status=IN_REVIEW&pageSize=100"),
    staleTime: STALE_TIME,
  });

  const summaryRaw = (summaryRes?.data as unknown) as Record<string, unknown> | null | undefined;
  const summary: DashboardSummary | null = summaryRaw
    ? {
        totalApplications:
          (summaryRaw.totalApplications as number | undefined) ??
          (summaryRaw.totalActiveEntities as number | undefined) ??
          0,
        pendingReview: (summaryRaw.pendingReview as number | undefined) ?? 0,
        approved: (summaryRaw.approved as number | undefined) ?? 0,
        highPlusRisk: (summaryRaw.highPlusRisk as number | undefined) ?? 0,
        alertMessage: summaryRaw.alertMessage as string | undefined,
      }
    : null;

  const statusDataRaw = (statusRes?.data as unknown) as { breakdown?: Record<string, number> } | StatusBreakdownItem[] | null | undefined;
  const statusBreakdown: StatusBreakdownItem[] = (() => {
    if (!statusDataRaw) return [];
    if (Array.isArray(statusDataRaw)) return statusDataRaw;
    const obj = statusDataRaw as { breakdown?: Record<string, number> };
    if (obj.breakdown && typeof obj.breakdown === "object") {
      return Object.entries(obj.breakdown).map(([status, count]) => ({
        status: status as AssessmentStatus,
        count,
      }));
    }
    return [];
  })();

  const distDataRaw = (distRes?.data as unknown) as { distribution?: Record<string, number> } | RiskDistributionItem[] | null | undefined;
  const riskDist: RiskDistributionItem[] = (() => {
    if (!distDataRaw) return [];
    if (Array.isArray(distDataRaw)) return distDataRaw;
    const obj = distDataRaw as { distribution?: Record<string, number> };
    if (obj.distribution && typeof obj.distribution === "object") {
      const distObj = obj.distribution;
      const total = Object.values(distObj).reduce((s, v) => s + v, 0);
      return Object.entries(distObj).map(([level, count]) => ({
        level: level as RiskLevel,
        count,
        percentage: total > 0 ? (count / total) * 100 : 0,
      }));
    }
    return [];
  })();

  const topRisksRaw = Array.isArray(topRes?.data) ? topRes.data : [];
  const topRisks: TopRiskItem[] = (topRisksRaw as unknown[]).map((raw) => {
    const item = raw as Record<string, unknown>;
    return {
      entityId: (item.entityId as string) ?? "",
      entityName: (item.entityName as string) ?? "",
      riskLevel: ((item.riskLevel ?? item.overallLevel) as RiskLevel) ?? "LOW",
      score: toRiskScore(((item.score ?? item.overallScore) as number) ?? 0),
      assessmentId: item.assessmentId as string | undefined,
    };
  });

  const recentActivityRaw = Array.isArray(actRes?.data) ? actRes.data : [];
  const recentActivity: ActivityItem[] = (recentActivityRaw as unknown[]).map((raw) => {
    const item = raw as Record<string, unknown>;
    return {
      id: (item.id as string) ?? "",
      action: (item.action as string) ?? "",
      resourceType: (item.resourceType as string) ?? "",
      resourceId: (item.resourceId as string) ?? "",
      userId: ((item.userId ?? item.actorId) as string) ?? "",
      userName: ((item.userName ?? item.actorId) as string) ?? t("common.unknown"),
      createdAt: ((item.createdAt ?? item.timestamp) as string) ?? "",
    };
  });

  const needsAttentionRaw: AssessmentListApiRow[] = [
    ...(Array.isArray(submittedRes?.data) ? submittedRes.data : []),
    ...(Array.isArray(inReviewRes?.data) ? inReviewRes.data : []),
  ];
  const needsAttention: NeedsAttentionItem[] = needsAttentionRaw
    .map((a) => ({
      assessmentId: a.id,
      entityName: a.entity?.name ?? "-",
      schemaName:
        a.schemaVersion?.schema?.name ??
        (a.schemaVersion?.version != null ? `v${a.schemaVersion.version}` : "-"),
      status: a.status,
      assignedTo: a.assignedTo ?? undefined,
      assignedToName: a.assignedToName ?? undefined,
      updatedAt: a.updatedAt,
    }))
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

  const attentionLoading = submittedLoading || inReviewLoading;

  const isLoading = summaryLoading || distLoading || statusLoading || topLoading || actLoading;

  const barData = statusBreakdown.map((item) => ({
    name: statusLabel(item.status),
    value: item.count,
  }));

  const pieData = riskDist.map((item) => ({
    ...item,
    name: RISK_LABELS[item.level],
    fill: RISK_COLORS[item.level],
  }));

  return (
    <>
      {/* Alert banner */}
      {summary?.alertMessage && (
        <ContextBanner
          variant="warning"
          title={t("dashboard.attentionRequired")}
          message={summary.alertMessage}
          dismissible
        />
      )}
      {summaryError && (
        <ContextBanner
          variant="warning"
          title={t("dashboard.dataLoadWarning")}
          message={t("dashboard.partialResults")}
          dismissible
        />
      )}

      {/* Page header */}
      <div className="flex items-start justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="text-[24px] font-semibold text-text-primary">{t("dashboard.title")}</h1>
          <p className="text-body text-text-secondary">
            {t("dashboard.subtitle")}
          </p>
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-4 gap-4">
        <StatCard
          icon={Box}
          label={t("dashboard.totalApps")}
          value={isLoading ? "-" : (summary?.totalApplications ?? 0)}
        />
        <StatCard
          icon={ClipboardCheck}
          label={t("dashboard.pendingReview")}
          value={isLoading ? "-" : (summary?.pendingReview ?? 0)}
        />
        <StatCard
          icon={CheckCircle2}
          label={t("dashboard.approved")}
          value={isLoading ? "-" : (summary?.approved ?? 0)}
        />
        <StatCard
          icon={ShieldAlert}
          label={t("dashboard.highRisk")}
          value={isLoading ? "-" : (summary?.highPlusRisk ?? 0)}
          valueColor="var(--danger)"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-5 gap-4">
        {/* Bar Chart — Applications by Status (~60%) */}
        <div className="col-span-3 flex flex-col gap-3 rounded-card border border-border-default bg-card p-5">
          <h2 className="text-[16px] font-medium text-text-primary">
            {t("dashboard.applicationsByStatus")}
          </h2>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={barData} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="var(--border-subtle)"
                  vertical={false}
                />
                <XAxis
                  dataKey="name"
                  tick={{ fill: "var(--text-muted)", fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: "var(--text-muted)", fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  content={<BarTooltipContent />}
                  cursor={{ fill: "var(--bg-hover)" }}
                />
                <Bar dataKey="value" fill="var(--accent)" radius={[4, 4, 0, 0]} maxBarSize={48} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Donut Chart — Risk Distribution (~40%) */}
        <div className="col-span-2 flex flex-col gap-3 rounded-card border border-border-default bg-card p-5">
          <div className="flex flex-col gap-0.5">
            <h2 className="text-[16px] font-medium text-text-primary">
              {t("dashboard.riskDistribution")}
            </h2>
            <p className="text-[13px] text-text-muted">
              {t("dashboard.applicationsByRiskLevel")}
            </p>
          </div>
          <div className="flex items-center gap-5">
            <div className="h-40 w-40 flex-shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData.length > 0 ? pieData : [{ name: t("common.noData"), count: 1, fill: "var(--border-default)", level: "LOW" as RiskLevel, percentage: 0 }]}
                    cx="50%"
                    cy="50%"
                    innerRadius={44}
                    outerRadius={72}
                    dataKey="count"
                    paddingAngle={pieData.length > 1 ? 3 : 0}
                    cornerRadius={4}
                    stroke="var(--card)"
                    strokeWidth={2}
                  >
                    {(pieData.length > 0 ? pieData : [{ fill: "var(--border-default)" }]).map(
                      (entry, idx) => (
                        <Cell key={idx} fill={entry.fill} />
                      )
                    )}
                  </Pie>
                  <Tooltip content={<PieTooltipContent />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            {/* Legend */}
            <div className="flex flex-1 flex-col gap-2.5">
              {pieData.map((item) => (
                <div key={item.level} className="flex items-center gap-2.5">
                  <span
                    className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-full"
                    style={{ backgroundColor: item.fill }}
                  />
                  <span className="text-[14px] text-text-primary">
                    {item.name}
                  </span>
                  <span className="ml-auto text-[14px] tabular-nums text-text-muted">
                    {item.count}
                  </span>
                </div>
              ))}
              {pieData.length === 0 && (
                <span className="text-caption text-text-muted">{t("common.noData")}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Highest Risk Applications */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h2 className="text-[16px] font-medium text-text-primary">
            {t("dashboard.highestRiskApps")}
          </h2>
          <span className="text-caption text-text-muted">{t("dashboard.top10")}</span>
        </div>
        <DataTable
          columns={topRisksColumns}
          data={topRisks.slice(0, 10)}
          isLoading={isLoading}
          emptyState={{
            icon: AlertTriangle,
            title: t("dashboard.noRiskData"),
            description: t("dashboard.completeAssessmentsForRisk"),
          }}
        />
      </div>

      {/* Needs Attention */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h2 className="text-[16px] font-medium text-text-primary">
            {t("dashboard.needsAttention")}
          </h2>
          <span className="text-caption text-text-muted">
            {t("dashboard.submittedAndInReview")}
          </span>
        </div>
        <DataTable
          columns={attentionColumns}
          data={needsAttention}
          isLoading={attentionLoading}
          emptyState={{
            icon: CheckCircle2,
            title: t("dashboard.allClear"),
            description: t("dashboard.noReviewPending"),
          }}
        />
      </div>

      {/* Recent Activity */}
      <div className="flex flex-col gap-3">
        <h2 className="text-[16px] font-medium text-text-primary">
          {t("dashboard.recentActivity")}
        </h2>
        <DataTable
          columns={activityColumns}
          data={recentActivity}
          isLoading={isLoading}
          emptyState={{
            icon: ClipboardCheck,
            title: t("dashboard.noRecentActivity"),
            description: t("dashboard.activityWillAppear"),
          }}
        />
      </div>
    </>
  );
}
